import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Alert,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Assessment as AssessmentIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  PeopleAlt as PeopleIcon
} from '@mui/icons-material';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { analyticsApi } from '../../services/api';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

function AdvancedAnalytics({ filters }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [closeReason, setCloseReason] = useState('');

  useEffect(() => {
    fetchUsersData();
  }, [filters]);

  const fetchUsersData = async () => {
    try {
      setLoading(true);
      const params = {
        platform: filters.platform,
        days: filters.days,
        start_date: filters.startDate?.toISOString(),
        end_date: filters.endDate?.toISOString()
      };
      
      const data = await analyticsApi.getUsers(params);
      // Filter out WEB_DASHBOARD users (dashboard login users)
      const socialUsers = (data.users || []).filter(user => 
        user.platform && user.platform.toUpperCase() !== 'WEB_DASHBOARD'
      );
      setUsers(socialUsers);
    } catch (error) {
      console.error('Error fetching users data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateAdvancedReport = async (reportType) => {
    try {
      setReportLoading(true);
      const params = {
        platform: filters.platform,
        start_date: filters.startDate?.toISOString(),
        end_date: filters.endDate?.toISOString(),
        days: filters.days
      };
      
      const data = await analyticsApi.generateReport(reportType, params);
      setReportData(data);
    } catch (error) {
      console.error('Error generating report:', error);
    } finally {
      setReportLoading(false);
    }
  };

  const handleCloseConversation = async () => {
    if (!selectedConversation) return;
    
    try {
      await analyticsApi.closeConversation(selectedConversation.id, closeReason);
      setCloseDialogOpen(false);
      setSelectedConversation(null);
      setCloseReason('');
      // Refresh data
      fetchUsersData();
    } catch (error) {
      console.error('Error closing conversation:', error);
    }
  };

  const getUserEngagementLevel = (user) => {
    const avgMessages = user.total_messages / Math.max(user.total_conversations, 1);
    if (avgMessages > 10) return { level: 'Alto', color: 'success' };
    if (avgMessages > 5) return { level: 'Medio', color: 'warning' };
    return { level: 'Basso', color: 'error' };
  };

  const getPlatformData = () => {
    const platformCount = users.reduce((acc, user) => {
      acc[user.platform] = (acc[user.platform] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(platformCount).map(([platform, count]) => ({
      name: platform.charAt(0).toUpperCase() + platform.slice(1),
      value: count
    }));
  };

  const getEngagementData = () => {
    const engagementLevels = users.reduce((acc, user) => {
      const engagement = getUserEngagementLevel(user);
      acc[engagement.level] = (acc[engagement.level] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(engagementLevels).map(([level, count]) => ({
      name: level,
      value: count
    }));
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <AssessmentIcon color="primary" />
            <Typography variant="h6">Caricamento Analytics Avanzate...</Typography>
          </Box>
          <LinearProgress />
        </CardContent>
      </Card>
    );
  }

  return (
    <Grid container spacing={3}>
      {/* Report Generation Section */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Box>
                <Typography variant="h6">
                  📊 Generazione Report Avanzati
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                  📱 Analytics delle piattaforme social (Telegram, WhatsApp, Instagram)
                </Typography>
              </Box>
              <Box>
                <Button
                  variant="outlined"
                  onClick={() => generateAdvancedReport('summary')}
                  disabled={reportLoading}
                  sx={{ mr: 1 }}
                >
                  Report Riepilogo
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => generateAdvancedReport('detailed')}
                  disabled={reportLoading}
                >
                  Report Dettagliato
                </Button>
              </Box>
            </Box>
            
            {reportLoading && <LinearProgress sx={{ mb: 2 }} />}
            
            {reportData && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Report generato con successo! ID: {reportData.report_id}
              </Alert>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Platform Distribution */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Distribuzione per Piattaforma
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={getPlatformData()}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {getPlatformData().map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Grid>

      {/* Engagement Levels */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Livelli di Coinvolgimento
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={getEngagementData()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Grid>

      {/* Top Users Table */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Box>
                <Typography variant="h6">
                  👥 Utenti Più Attivi
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                  📱 Solo utenti delle piattaforme social
                </Typography>
              </Box>
              <Button
                startIcon={<DownloadIcon />}
                onClick={() => analyticsApi.exportConversations('csv', filters)}
                size="small"
              >
                Esporta
              </Button>
            </Box>
            
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Utente</TableCell>
                    <TableCell>Piattaforma</TableCell>
                    <TableCell align="center">Conversazioni</TableCell>
                    <TableCell align="center">Messaggi</TableCell>
                    <TableCell align="center">Risposte AI</TableCell>
                    <TableCell align="center">Engagement</TableCell>
                    <TableCell align="center">Ultima Attività</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.slice(0, 10).map((user) => {
                    const engagement = getUserEngagementLevel(user);
                    return (
                      <TableRow key={user.id} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {user.username || user.first_name || 'Utente Anonimo'}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              ID: {user.platform_user_id}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={user.platform.toUpperCase()} 
                            size="small"
                            color={user.platform === 'telegram' ? 'primary' : 
                                   user.platform === 'whatsapp' ? 'success' : 'secondary'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                            <PeopleIcon fontSize="small" />
                            {user.total_conversations}
                          </Box>
                        </TableCell>
                        <TableCell align="center">{user.total_messages}</TableCell>
                        <TableCell align="center">{user.total_ai_responses}</TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={engagement.level} 
                            size="small"
                            color={engagement.color}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="caption">
                            {user.last_interaction ? 
                              new Date(user.last_interaction).toLocaleDateString() : 
                              'Mai'
                            }
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>

      {/* Close Conversation Dialog */}
      <Dialog open={closeDialogOpen} onClose={() => setCloseDialogOpen(false)}>
        <DialogTitle>Chiudi Conversazione</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Motivo chiusura (opzionale)"
            value={closeReason}
            onChange={(e) => setCloseReason(e.target.value)}
            multiline
            rows={3}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseDialogOpen(false)}>Annulla</Button>
          <Button onClick={handleCloseConversation} variant="contained">
            Chiudi Conversazione
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}

export default AdvancedAnalytics;
